import React, { createRef, useMemo, useState } from 'react'
import { Alert, Button, Col, Flex, Row, Typography } from 'antd'
import { useForm, SubmitHandler, Controller } from 'react-hook-form'
import { InfoCircle, DynamicColorRefreshIcon, DownExpandIcon, UpCollapseIcon } from 'src/assets/icons'
import './index.scss'
import { Collapse } from 'antd'

import { useTranslation } from 'react-i18next'

import CustomInput from 'src/app/shared/components/common/input'
import { inputDecimalNumber, inputNumber } from 'src/app/shared/utils/Number.util'
import { SYSTEM_MESSAGE_IDS } from 'src/app/shared/constants/systemMessages.constants'
import { checkVerification } from 'src/app/shared/services/TransactionService'
import { ICheckVerificationload } from 'src/app/shared/models/Transaction.model'
import { IDefaultResponse } from 'src/app/shared/models/auth.model'
import CommonTable from 'src/app/shared/components/common/table'
import { colorBlack } from 'src/app/themes/primaryThemeDefault'
import { TableColumnDefs } from 'src/app/shared/components/common/new-table/table.model'
import NewCommonTable from 'src/app/shared/components/common/new-table'

const CheckVerification: React.FC = () => {
  const { t } = useTranslation()

  const [loading, setLoading] = useState(false)

  const [messagesResult, setMessagesResult] = useState<any>([])
  const [resultRowData, setResultRowData] = useState<any>(null)
  const [verificationHistoryRowData, setVerificationHistoryRowData] = useState<any>([])
  const [usersAssignedRowData, setUsersAssignedRowData] = useState<any>([])

  const initForm: ICheckVerificationload = {
    accountNumber: '',
    checkNumber: '',
    amount: null,
  }

  const {
    handleSubmit,
    control,
    formState: { isValid, isDirty },
    reset,
  } = useForm<ICheckVerificationload>({ mode: 'all', defaultValues: initForm })

  const onSubmit: SubmitHandler<ICheckVerificationload> = (data) => {
    let payload: ICheckVerificationload = {
      accountNumber: data?.accountNumber ?? '',
      checkNumber: data?.checkNumber ?? '',
    }

    if (data?.amount) {
      payload.amount = data?.amount
    }

    setLoading(true)
    checkVerification(payload)
      .then(
        (res: any) => {
          if (res?.data) {
            if (res?.data?.verificationResult) {
              setResultRowData([res?.data?.verificationResult])
            } else {
              setResultRowData([])
            }

            if (res?.data?.messages) {
              setMessagesResult(res?.data?.messages)
            }

            if (res?.data?.verificationHistories?.length) {
              setTimeout(() => {
                setVerificationHistoryRowData(res?.data?.verificationHistories)
              }, 500)
            } else {
              setVerificationHistoryRowData([])
            }

            if (res?.data?.userAssigned?.length) {
              setTimeout(() => {
                setUsersAssignedRowData(res?.data?.userAssigned)
              }, 1000)
            } else {
              setUsersAssignedRowData([])
            }
          }
        },
        (err: IDefaultResponse<any>) => {},
      )
      .finally(() => {
        setLoading(false)
      })
  }

  const checkResultsHeaders = 'CheckVerification:checkVerificationResults.fields'
  const checkVerificationResultsColumnDefs: TableColumnDefs[] = useMemo(
    (): TableColumnDefs[] => [
      {
        colId: 'clientName',
        field: 'clientName',
        headerName: t(`${checkResultsHeaders}.clientName`),
        hide: false,
        defaultSelect: true,
        minWidth: 300,
      },
      {
        colId: 'accountNumber',
        field: 'accountNumber',
        headerName: t(`${checkResultsHeaders}.accountNumber`),
        hide: false,
        defaultSelect: true,
        minWidth: 120,
      },
      {
        colId: 'accountName',
        field: 'accountName',
        headerName: t(`${checkResultsHeaders}.accountName`),
        hide: false,
        defaultSelect: true,
      },
      {
        colId: 'issuedDate',
        field: 'issuedDate',
        headerName: t(`${checkResultsHeaders}.issuedDate`),
        hide: false,
        defaultSelect: true,
        cellRenderer: (data: any) => {
          return (
            <Typography.Text
              style={{
                fontSize: 14,
                fontFamily: 'MyriadPro',
              }}
            >
              {data?.data?.issuedDate ?? ''}
            </Typography.Text>
          )
        },
        minWidth: 120,
      },
      {
        colId: 'checkNumber',
        field: 'checkNumber',
        headerName: t(`${checkResultsHeaders}.checkNumber`),
        hide: false,
        defaultSelect: true,
        minWidth: 120,
      },
      {
        colId: 'issuedPayee',
        field: 'issuedPayee',
        headerName: t(`${checkResultsHeaders}.issuedPayee`),
        hide: false,
        defaultSelect: true,
        minWidth: 120,
      },
      {
        colId: 'amount',
        field: 'amount',
        headerName: t(`${checkResultsHeaders}.amount`),
        hide: false,
        defaultSelect: true,
        minWidth: 120,
        flex: 1,
      },
    ],
    [t],
  )

  const verificationHistoryHeaders = 'CheckVerification:showVerificationHistory.fields'
  const verificationHistoryHeadersColumnDefs: TableColumnDefs[] = useMemo(
    (): TableColumnDefs[] => [
      {
        colId: 'userVerified',
        field: 'userVerified',
        headerName: t(`${verificationHistoryHeaders}.userVerified`),
        hide: false,
        defaultSelect: true,
        minWidth: 300,
      },
      {
        colId: 'dateVerified',
        field: 'dateVerified',
        headerName: t(`${verificationHistoryHeaders}.dateVerified`),
        hide: false,
        defaultSelect: true,
        cellRenderer: (data: any) => {
          return (
            <Typography.Text
              style={{
                fontSize: 14,
                fontFamily: 'MyriadPro',
              }}
            >
              {data?.data?.dateVerified ?? ''}
            </Typography.Text>
          )
        },
        minWidth: 300,
      },
      {
        colId: 'results',
        field: 'results',
        headerName: t(`${verificationHistoryHeaders}.results`),
        hide: false,
        defaultSelect: true,
        minWidth: 600,
        cellRenderer: (data: any) => {
          return (
            <Typography.Text
              style={{
                fontSize: 14,
                fontFamily: 'MyriadPro',
              }}
            >
              {data?.data?.results?.join(', ')}
            </Typography.Text>
          )
        },
      },
    ],
    [t],
  )

  const usersAssignedHeaders = 'CheckVerification:showUsersAssignedToThisClient.fields'
  const usersAssignedColumnDefs: TableColumnDefs[] = useMemo(
    (): TableColumnDefs[] => [
      {
        colId: 'displayName',
        field: 'displayName',
        headerName: t(`${usersAssignedHeaders}.displayName`),
        hide: false,
        defaultSelect: true,
        minWidth: 300,
      },
      {
        colId: 'primaryPhoneNumber',
        field: 'primaryPhoneNumber',
        headerName: t(`${usersAssignedHeaders}.primaryPhoneNumber`),
        hide: false,
        defaultSelect: true,
        minWidth: 180,
      },
      {
        colId: 'mobilePhoneNumber',
        field: 'mobilePhoneNumber',
        headerName: t(`${usersAssignedHeaders}.mobilePhoneNumber`),
        hide: false,
        defaultSelect: true,
        minWidth: 180,
      },
      {
        colId: 'emailAddress',
        field: 'emailAddress',
        headerName: t(`${usersAssignedHeaders}.emailAddress`),
        hide: false,
        defaultSelect: true,
        minWidth: 300,
      },
    ],
    [t],
  )

  const handleOnRefreshFilter = () => {
    reset(initForm)
  }

  const collapseItems = useMemo(() => {
    let arr: any = []

    if (verificationHistoryRowData?.length) {
      arr = [
        ...arr,
        {
          key: '1',
          label: (
            <Typography.Text className='collapse-title'>
              {t('CheckVerification:showVerificationHistory.title')}
            </Typography.Text>
          ),
          children: (
            <div>
              <NewCommonTable
                onColumnResized={() => {}}
                key={'verification-history-table'}
                className='verification-history-table'
                suppressRowVirtualisation
                columnDefs={verificationHistoryHeadersColumnDefs}
                rowData={verificationHistoryRowData}
                disableTableControl
                displayActionColumn={false}
                handleSearch={async () => {}}
                autoSort={false}
              />
            </div>
          ),
        },
      ]
    }

    if (usersAssignedRowData?.length) {
      arr = [
        ...arr,
        {
          key: '2',
          label: (
            <Typography.Text className='collapse-title'>
              {t('CheckVerification:showUsersAssignedToThisClient.title')}
            </Typography.Text>
          ),
          children: (
            <div>
              <NewCommonTable
                onColumnResized={() => {}}
                key={'users-assigned-table'}
                className='users-assigned-table'
                suppressRowVirtualisation
                columnDefs={usersAssignedColumnDefs}
                rowData={usersAssignedRowData}
                disableTableControl
                displayActionColumn={false}
                handleSearch={async () => {}}
                autoSort={false}
              />
            </div>
          ),
        },
      ]
    }

    return arr
  }, [verificationHistoryHeadersColumnDefs, verificationHistoryRowData, usersAssignedColumnDefs, usersAssignedRowData])

  return (
    <div className='check-verification-container'>
      <form className='w-100' onSubmit={handleSubmit(onSubmit)}>
        <Flex className='section-check-verify'>
          <div className='section-check-verify-input'>
            <div className='check-verify-input'>
              <Controller
                control={control}
                name='accountNumber'
                render={({ field, fieldState }) => (
                  <CustomInput
                    value={field.value}
                    label={t('CheckVerification:accountNumber')}
                    placeholder={t('CheckVerification:placeHolder.accountNumber')}
                    onChange={(e) => field.onChange(inputNumber(e), { shouldValidate: true })}
                    onBlur={field.onBlur}
                    error={fieldState.error}
                    maxLength={10}
                    required
                  />
                )}
                rules={{
                  required: {
                    value: true,
                    message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.FIELD_IS_REQUIRED, {
                      Required_Field: t('CheckVerification:accountNumber'),
                    }),
                  },
                }}
              />
            </div>

            <div className='check-verify-input'>
              <Controller
                control={control}
                name='checkNumber'
                render={({ field, fieldState }) => (
                  <CustomInput
                    value={field.value}
                    label={t('CheckVerification:checkNumber')}
                    placeholder={t('CheckVerification:placeHolder.checkNumber')}
                    onChange={(e) => field.onChange(inputNumber(e), { shouldValidate: true })}
                    onBlur={field.onBlur}
                    maxLength={12}
                    error={fieldState.error}
                    required
                  />
                )}
                rules={{
                  required: {
                    value: true,
                    message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.FIELD_IS_REQUIRED, {
                      Required_Field: t('CheckVerification:checkNumber'),
                    }),
                  },
                }}
              />
            </div>

            <div className='check-verify-input'>
              <Controller
                control={control}
                name='amount'
                render={({ field, fieldState }) => (
                  <CustomInput
                    style={{ display: 'flex', alignItems: 'flex-end', marginTop: 7 }}
                    label={t('CheckVerification:amount')}
                    placeholder={t('CheckVerification:placeHolder.amount')}
                    value={field.value}
                    status={fieldState?.error ? 'error' : ''}
                    onChange={(e) => field.onChange(inputDecimalNumber(e, 14, 2), { shouldValidate: true })}
                    onBlur={field.onBlur}
                  />
                )}
              />
            </div>
          </div>
          <div className='check-verify-actions'>
            <Button size='large' onClick={handleOnRefreshFilter} disabled={!isDirty || loading}>
              <DynamicColorRefreshIcon />
            </Button>
            <Button
              size='large'
              type='primary'
              style={{ width: 200 }}
              onClick={handleSubmit(onSubmit)}
              loading={loading}
              disabled={!isValid || loading}
            >
              {t('CheckVerification:btn_verifyCheck')}
            </Button>
          </div>
        </Flex>

        {messagesResult?.length ? (
          <Flex vertical className='message-block'>
            <Alert
              icon={<InfoCircle />}
              style={{ borderRadius: 12, padding: '12px 20px 0px 20px' }}
              showIcon
              message={
                <Typography.Text
                  style={{
                    fontSize: '16px',
                    fontWeight: 700,
                    margin: 0,
                  }}
                >
                  {t('common:message')}
                </Typography.Text>
              }
              description={
                <ul style={{ paddingInlineStart: '17px' }}>
                  {messagesResult?.map((msgItem: any) => (
                    <li style={{ fontSize: '16px', color: colorBlack }}>{msgItem}</li>
                  ))}
                </ul>
              }
              type='error'
            />
          </Flex>
        ) : null}

        <div
          className='check-verification-results-section'
          style={{
            display: resultRowData === null ? 'none' : undefined,
          }}
        >
          <Typography.Text
            style={{
              fontSize: '18px',
              lineHeight: '26px',
              fontWeight: 600,
            }}
          >
            {t('CheckVerification:checkVerificationResults.title')}
          </Typography.Text>
          <NewCommonTable
            onColumnResized={() => {}}
            className={`${resultRowData?.length ? 'table-not-empty' : ''}`}
            columnDefs={checkVerificationResultsColumnDefs}
            rowData={resultRowData}
            loading={false}
            disableTableControl
            displayActionColumn={false}
            handleSearch={async () => {}}
            autoSort={false}
          />
        </div>

        {collapseItems?.length ? (
          <div className='collapse-container'>
            <Collapse
              expandIcon={({ isActive }) => (isActive ? <DownExpandIcon /> : <UpCollapseIcon />)}
              expandIconPosition='right'
              accordion
              defaultActiveKey={['1']}
              items={collapseItems}
            ></Collapse>
          </div>
        ) : null}
      </form>
    </div>
  )
}
export default CheckVerification
